import React, { useState, useEffect } from 'react';
import { Spinner, Table, NavLink } from 'reactstrap';
import { connect } from 'react-redux';
import * as RRD from 'react-router-dom';
import { FieldPath } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/sv';

import { SortService, SortInstruction } from 'services/sortService';
import { IOrderReference, OrderStatus, OrderType } from 'models/Order';
import './AFSFiles.css';

dayjs.extend(relativeTime);
dayjs.extend(timezone);
dayjs.locale('sv');

export type IOrderListProps = {
  items?: IOrderReference[];
  onCopyClick?: (order: IOrderReference) => void;
  enableFetchMore?: boolean;
  onFetchMore?: () => void;
};

const OrderList = function ({ items, onCopyClick, onFetchMore, enableFetchMore }: IOrderListProps) {
  const { t } = useTranslation('translation');
  const sortService = SortService<IOrderReference>();
  const [sortedItems, setSortedItems] = useState<IOrderReference[]>(
    !!items ? sortService.sortItems(items, { value: 'createdDate', asc: false }) : []
  );
  const [sortInstruction, setSortInstruction] = useState<SortInstruction<IOrderReference>>({
    value: 'createdDate',
    asc: false,
  });
  const [sortedColumn, setSortedColumn] = useState('orderDate');

  useEffect(() => {
    setSortedItems(!!items ? sortService.sortItems(items, sortInstruction) : []);
  }, [items]);

  const onHeaderClick = (
    columnName: string,
    value: FieldPath<IOrderReference> | ((item: IOrderReference) => any)
  ) => {
    const _sortInstruction = {
      value: value,
      asc: sortedColumn === columnName ? !sortInstruction.asc : true,
    };
    setSortInstruction(_sortInstruction);
    const _sortedItems = !!items ? sortService.sortItems([...items], _sortInstruction) : [];
    setSortedItems(_sortedItems);
    setSortedColumn(columnName);
  };

  const getClass: (columnName: string) => string = (columnName: string) => {
    return (
      'sortable' +
      (sortedColumn === columnName ? ' sorted' + (sortInstruction.asc ? ' asc' : ' desc') : '')
    );
  };

  return (
    <>
      <div className="row">
        <div className="col collapsable-table-md">
          <Table hover className=" default" striped={true}>
            <thead>
              <tr>
                <th
                  className={getClass('orderNumber')}
                  onClick={() => onHeaderClick('orderNumber', 'orderNumber')}
                >
                  {t('orders.ORDER_ID')}
                </th>
                <th
                  className={getClass('orderDate')}
                  onClick={() => onHeaderClick('createdDate', 'createdDate')}
                >
                  {t('orders.ORDER_DATE')}
                </th>
                <th
                  className={getClass('orderStatus')}
                  onClick={() => onHeaderClick('orderStatus', 'orderStatus')}
                >
                  {t('orders.ORDER_STATUS')}
                </th>
                <th
                  className={getClass('organization')}
                  onClick={() => onHeaderClick('customerUnitName', 'customerUnitName')}
                >
                  {t('orders.ORDER_ORGANIZATION')}
                </th>
                <th
                  className={getClass('orderType')}
                  onClick={() => onHeaderClick('orderType', 'orderType')}
                >
                  {t('orders.ORDER_TYPE')}
                </th>
                <th
                  className={getClass('customerContact')}
                  onClick={() => onHeaderClick('customerContact', 'customerContact')}
                >
                  {t('orders.ORDER_CLIENT')}
                </th>
                <th
                  className={getClass('employeeName')}
                  onClick={() => onHeaderClick('employeeName', 'employeeName')}
                >
                  {t('orders.ORDER_EMPLOYEE')}
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((i: IOrderReference) => (
                <tr key={i.id}>
                  <td>
                    <span className="collapsed-title">{t('orders.ORDER_ID')}: </span>
                    <NavLink
                      tag={RRD.NavLink}
                      to={
                        '/service/order/' +
                        Object.values(OrderType)[i.orderType].toLowerCase() +
                        '/' +
                        i.id
                      }
                      style={{
                        textDecoration: 'underline',
                        fontWeight: 800,
                      }}
                    >
                      {i.orderNumber}
                    </NavLink>
                  </td>
                  <td>
                    <span className="collapsed-title">{t('orders.ORDER_DATE')}: </span>{' '}
                    {dayjs(i.createdDate).format('YYYY-MM-DD HH:mm')}
                  </td>
                  <td>
                    <span className="collapsed-title">{t('orders.ORDER_STATUS')}: </span>{' '}
                    {t(
                      'orders.STATUS_' +
                        Object.values(OrderStatus)[i.orderStatus].replace(/\s/g, '')
                    )}
                  </td>
                  <td>
                    <span className="collapsed-title">{t('orders.ORDER_ORGANIZATION')}: </span>{' '}
                    {i.customerUnitName} ({i.customerUnitId})
                  </td>
                  <td>
                    <span className="collapsed-title">{t('orders.ORDER_TYPE')}: </span>
                    {t('orders.TYPE_' + Object.values(OrderType)[i.orderType])}
                  </td>
                  <td>
                    <span className="collapsed-title">{t('orders.ORDER_CLIENT')}: </span>{' '}
                    {i.customerContact}
                  </td>
                  <td>
                    <span className="collapsed-title">{t('orders.ORDER_EMPLOYEE')}: </span>{' '}
                    {i.orderType === 0 ? i.employeeName : 'N/A'}
                  </td>
                  <td>
                    <a
                      href="/"
                      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onCopyClick && onCopyClick(i);
                      }}
                    >
                      {t('orders.COPY')}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
      <div className="row footer">
        <div className="col-12 text-center loading-more-indicator-container col-12">
          {enableFetchMore && (
            <div
              className="col-12 text-center btn-primary"
              onClick={() => {
                onFetchMore && onFetchMore();
              }}
            >
              {t('orders.FETCH_MORE')}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default connect()(OrderList);
